import { getMapV2PinColor } from "./mapV2Styles";
import { getThemeToken } from "../theme/runtimeTheme";

const CACHE = new Map();
const CACHE_VER = "v3-teardrop-pin";

const PIN_PATH =
  "M40 12 C28 12 18 22 18 34 C18 48 40 72 40 72 S62 48 62 34 C62 22 52 12 40 12 Z";

const MAP_V2_PIN_PALETTE = ["#5BA8FF", "#7DD49A", "#F4A05B", "#C28BE6"];

function selectedHaloColor() {
  return getThemeToken("primary") || "#ea580c";
}

function palettePinFill(index) {
  return MAP_V2_PIN_PALETTE[Math.abs(index) % MAP_V2_PIN_PALETTE.length];
}

function escapeXml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function truncatePinLabel(title, maxLen = 28) {
  const t = String(title || "")
    .trim()
    .replace(/\s+/g, " ");
  if (!t) return "Property";
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(1, maxLen - 1))}…`;
}

/** Full hotspot title for map labels (no ellipsis). */
export function normalizeHotspotPinLabel(title) {
  const t = String(title || "")
    .trim()
    .replace(/\s+/g, " ");
  return t || "Place";
}

/** Auto label box width from text (12px bold, single line). */
export function estimatePinLabelWidthPx(labelText, { min = 72, max = 340 } = {}) {
  const t = normalizeHotspotPinLabel(labelText);
  const width = Math.ceil(t.length * 7.25) + 28;
  return Math.min(max, Math.max(min, width));
}

/** Teardrop map pin for Google Maps MarkerF — anchor at pin tip (matches mobile Map V2). */
const MAP_PIN_IDLE = "#64748b";

export function getMapDotPinSvg(index, isSelected, isHovered = false, faded = false, fillColor = null) {
  const fill =
    fillColor ||
    (isSelected ? getMapV2PinColor(index, true) : MAP_PIN_IDLE);
  const key = `${CACHE_VER}|grey|${isSelected ? 1 : 0}|${faded ? 1 : 0}|${fill}`;
  if (CACHE.has(key)) return CACHE.get(key);

  const haloR = isSelected ? 22 : 0;
  const haloOpacity = isSelected ? 0.14 : 0;
  const pinOpacity = faded ? 0.5 : 0.92;
  const halo =
    haloR > 0
      ? `<ellipse cx="40" cy="68" rx="${haloR}" ry="${Math.round(haloR * 0.55)}" fill="${selectedHaloColor()}" opacity="${haloOpacity}"/>`
      : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="88" viewBox="0 0 80 88">${halo}<path d="${PIN_PATH}" fill="${fill}" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round" opacity="${pinOpacity}"/><circle cx="40" cy="34" r="6" fill="#ffffff" opacity="0.92"/></svg>`;
  const encoded = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  const icon = {
    url: encoded,
    scaledSize: { width: 44, height: 48 },
    anchor: { x: 22, y: 44 },
  };
  CACHE.set(key, icon);
  return icon;
}

/** Selected teardrop + name callout above pin — primary fill. */
export function getMapV2LabeledTeardropPinSvg(index, title, fillColor = null) {
  const labelText = truncatePinLabel(title);
  const fill = fillColor || getMapV2PinColor(index, true);
  const key = `${CACHE_VER}|label-tear-v3|${labelText}|${fill}`;
  if (CACHE.has(key)) return CACHE.get(key);

  const labelW = Math.min(156, Math.max(80, Math.round(labelText.length * 7 + 20)));
  const pinW = 44;
  const pinH = 48;
  const labelBoxH = 24;
  const pointerH = 6;
  const stackGap = 4;
  const labelStackH = labelBoxH + pointerH + stackGap;
  const totalW = Math.max(pinW, labelW);
  const totalH = labelStackH + pinH;
  const labelX = (totalW - labelW) / 2;
  const pinX = (totalW - pinW) / 2;
  const pointerHalf = 7;
  const cx = totalW / 2;
  const pointerTop = labelBoxH;
  const pinY = labelStackH;
  const pinScale = pinW / 80;
  const textX = labelX + labelW / 2;
  const textY = 16;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}" viewBox="0 0 ${totalW} ${totalH}">
<rect x="${labelX}" y="0" width="${labelW}" height="${labelBoxH}" rx="8" fill="#ffffff" stroke="#e2e8f0" stroke-width="1"/>
<text x="${textX}" y="${textY}" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="12" font-weight="700" fill="#0f172a">${escapeXml(labelText)}</text>
<polygon points="${cx - pointerHalf},${pointerTop} ${cx + pointerHalf},${pointerTop} ${cx},${pointerTop + pointerH}" fill="#ffffff" stroke="#e2e8f0" stroke-width="1" stroke-linejoin="round"/>
<g transform="translate(${pinX} ${pinY}) scale(${pinScale})">
<path d="${PIN_PATH}" fill="${fill}" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round"/>
<circle cx="40" cy="34" r="6" fill="#ffffff" opacity="0.92"/>
</g>
</svg>`;

  const encoded = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  const icon = {
    url: encoded,
    scaledSize: { width: totalW, height: totalH },
    anchor: { x: Math.round(totalW / 2), y: totalH - 1 },
  };
  CACHE.set(key, icon);
  return icon;
}

/** Hotspot labeled pin — full name beside teardrop (label right of pin). */
export function getHotspotLabeledTeardropPinSvg(title, fillColor = null) {
  const labelText = normalizeHotspotPinLabel(title);
  const fill = fillColor || MAP_PIN_IDLE;
  const key = `${CACHE_VER}|hotspot-label-side-v3|${labelText}|${fill}`;
  if (CACHE.has(key)) return CACHE.get(key);

  const labelW = estimatePinLabelWidthPx(labelText);
  const pinW = 44;
  const pinH = 48;
  const labelBoxH = 26;
  const gap = 6;
  const labelPadX = 12;
  const totalW = pinW + gap + labelW;
  const totalH = pinH;
  const labelX = pinW + gap;
  const labelY = Math.round((totalH - labelBoxH) / 2);
  const pinScale = pinW / 80;
  const textX = labelX + labelPadX;
  const textY = labelY + 17;
  const connectorH = 8;
  const connectorY = labelY + (labelBoxH - connectorH) / 2;
  const connectorX = labelX;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}" viewBox="0 0 ${totalW} ${totalH}">
<g transform="translate(0 0) scale(${pinScale})">
<path d="${PIN_PATH}" fill="${fill}" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round"/>
<circle cx="40" cy="34" r="6" fill="#ffffff" opacity="0.92"/>
</g>
<polygon points="${connectorX},${connectorY + connectorH / 2} ${connectorX - 5},${connectorY} ${connectorX - 5},${connectorY + connectorH}" fill="#ffffff" stroke="#e2e8f0" stroke-width="1" stroke-linejoin="round"/>
<rect x="${labelX}" y="${labelY}" width="${labelW}" height="${labelBoxH}" rx="8" fill="#ffffff" stroke="#e2e8f0" stroke-width="1"/>
<text x="${textX}" y="${textY}" text-anchor="start" font-family="Arial,Helvetica,sans-serif" font-size="12" font-weight="700" fill="#0f172a">${escapeXml(labelText)}</text>
</svg>`;

  const encoded = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  const icon = {
    url: encoded,
    scaledSize: { width: totalW, height: totalH },
    anchor: { x: Math.round(pinW / 2), y: pinH - 1 },
  };
  CACHE.set(key, icon);
  return icon;
}

export function googleDotIconFromSvg(index, isSelected, isHovered, googleMaps, faded = false, fillColor = null) {
  const base = getMapDotPinSvg(index, isSelected, isHovered, faded, fillColor);
  if (!googleMaps?.maps?.Size || !googleMaps?.maps?.Point) return base;
  return {
    url: base.url,
    scaledSize: new googleMaps.maps.Size(base.scaledSize.width, base.scaledSize.height),
    anchor: new googleMaps.maps.Point(base.anchor.x, base.anchor.y),
  };
}

/** Red teardrop for project origin on profile hotspot maps. */
const PROJECT_PIN_RED = "#dc2626";

export function getProjectLocationPinSvg() {
  const key = `${CACHE_VER}|project-red-v1`;
  if (CACHE.has(key)) return CACHE.get(key);

  const halo = `<ellipse cx="40" cy="68" rx="22" ry="12" fill="${PROJECT_PIN_RED}" opacity="0.16"/>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="80" height="88" viewBox="0 0 80 88">${halo}<path d="${PIN_PATH}" fill="${PROJECT_PIN_RED}" stroke="#ffffff" stroke-width="2.5" stroke-linejoin="round" opacity="0.96"/><circle cx="40" cy="34" r="6" fill="#ffffff" opacity="0.92"/></svg>`;
  const encoded = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  const icon = {
    url: encoded,
    scaledSize: { width: 44, height: 48 },
    anchor: { x: 22, y: 44 },
  };
  CACHE.set(key, icon);
  return icon;
}

export function googleProjectLocationPinIcon(googleMaps) {
  const base = getProjectLocationPinSvg();
  if (!googleMaps?.maps?.Size || !googleMaps?.maps?.Point) return base;
  return {
    url: base.url,
    scaledSize: new googleMaps.maps.Size(base.scaledSize.width, base.scaledSize.height),
    anchor: new googleMaps.maps.Point(base.anchor.x, base.anchor.y),
  };
}

export function clearMapDotPinCache() {
  CACHE.clear();
}

const CLASSIC_CACHE_VER = "v5-black-idle-red-active";
/** Idle map pins — black fill, white border */
const CLASSIC_PIN_FILL_IDLE = "#1a1a1a";
const CLASSIC_PIN_STROKE_IDLE = "#ffffff";
/** Active / hover / selected pin — classic red */
const CLASSIC_PIN_FILL_ACTIVE = "#EA4335";
const CLASSIC_PIN_STROKE_ACTIVE = "#B31412";
const CLASSIC_PIN_PATH =
  "M14 2C9.03 2 5 6.03 5 11c0 6.25 9 19 9 19s9-12.75 9-19c0-4.97-4.03-9-9-9z";

function toGoogleIcon(base, googleMaps) {
  if (!googleMaps?.maps?.Size || !googleMaps?.maps?.Point) return base;
  return {
    url: base.url,
    scaledSize: new googleMaps.maps.Size(base.scaledSize.width, base.scaledSize.height),
    anchor: new googleMaps.maps.Point(base.anchor.x, base.anchor.y),
  };
}

/** Small map pin — idle (black + white border) unless active (red). */
export function getClassicMapPinSvg(isActive = false) {
  const key = `${CLASSIC_CACHE_VER}|pin|${isActive ? "active" : "idle"}`;
  if (CACHE.has(key)) return CACHE.get(key);

  const fill = isActive ? CLASSIC_PIN_FILL_ACTIVE : CLASSIC_PIN_FILL_IDLE;
  const stroke = isActive ? CLASSIC_PIN_STROKE_ACTIVE : CLASSIC_PIN_STROKE_IDLE;
  const w = 26;
  const h = 38;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 28 40">
  <path d="${CLASSIC_PIN_PATH}" fill="${fill}" stroke="${stroke}" stroke-width="${isActive ? 1.1 : 2}" stroke-linejoin="round"/>
  <circle cx="14" cy="11" r="3" fill="#ffffff"/>
</svg>`;
  const encoded = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  const icon = {
    url: encoded,
    scaledSize: { width: w, height: h },
    anchor: { x: 13, y: h - 1 },
  };
  CACHE.set(key, icon);
  return icon;
}

/**
 * Emphasized pin + project name label beside pin (location search / nearby map).
 * Anchor at pin tip. Full title with auto-width label pill.
 */
export function getClassicLabeledPinSvg(title) {
  const labelText = normalizeHotspotPinLabel(title);
  const key = `${CLASSIC_CACHE_VER}|label-side-v2|${labelText}`;
  if (CACHE.has(key)) return CACHE.get(key);

  const labelW = Math.min(300, Math.max(72, Math.ceil(labelText.length * 6.5) + 24));
  const pinW = 32;
  const gap = 6;
  const labelPadX = 10;
  const totalW = pinW + gap + labelW;
  const totalH = 50;
  const labelH = 26;
  const labelY = Math.round((totalH - labelH) / 2);
  const labelX = pinW + gap;
  const textX = labelX + labelPadX;
  const textY = labelY + 17;
  const pinScale = 1.12;
  const pinCx = 15;
  const pinCy = 20;
  const anchorX = Math.round(pinCx * pinScale);
  const connectorH = 8;
  const connectorY = labelY + (labelH - connectorH) / 2;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}" viewBox="0 0 ${totalW} ${totalH}">
  <defs>
    <filter id="pinShadow" x="-20%" y="-10%" width="140%" height="130%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000" flood-opacity="0.22"/>
    </filter>
  </defs>
  <g filter="url(#pinShadow)">
    <g transform="translate(${pinCx} ${pinCy}) scale(${pinScale}) translate(${-pinCx} ${-pinCy})">
      <path d="${CLASSIC_PIN_PATH}" fill="${CLASSIC_PIN_FILL_ACTIVE}" stroke="${CLASSIC_PIN_STROKE_ACTIVE}" stroke-width="1.1" stroke-linejoin="round"/>
      <circle cx="14" cy="11" r="3.4" fill="#ffffff"/>
    </g>
    <polygon points="${labelX},${connectorY + connectorH / 2} ${labelX - 4},${connectorY} ${labelX - 4},${connectorY + connectorH}" fill="#ffffff" stroke="#dadce0" stroke-width="1" stroke-linejoin="round"/>
    <rect x="${labelX}" y="${labelY}" width="${labelW}" height="${labelH}" rx="6" fill="#ffffff" stroke="#dadce0" stroke-width="1"/>
    <text x="${textX}" y="${textY}" text-anchor="start" font-family="Arial,Helvetica,sans-serif" font-size="12" font-weight="700" fill="#202124">${escapeXml(labelText)}</text>
  </g>
</svg>`;

  const encoded = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  const icon = {
    url: encoded,
    scaledSize: { width: totalW, height: totalH },
    anchor: { x: anchorX, y: totalH - 2 },
  };
  CACHE.set(key, icon);
  return icon;
}

/** Location search map: black idle pins; red + side label on hover or selection. */
export function googleLocationSearchPinIcon({
  title = "",
  showLabel = false,
  isSelected = false,
  isHovered = false,
  googleMaps,
}) {
  if (showLabel) {
    return toGoogleIcon(getClassicLabeledPinSvg(title), googleMaps);
  }
  return toGoogleIcon(getClassicMapPinSvg(isSelected || isHovered), googleMaps);
}

/** @deprecated Use getClassicMapPinSvg */
export function getClassicRedPinSvg() {
  return getClassicMapPinSvg(false);
}

/**
 * Map V2 listing pins — teardrop palette; selected pin shows name label (mobile parity).
 */
export function googleMapV2ListingPinIcon({
  index = 0,
  title = "",
  showLabel = false,
  isSelected = false,
  isHovered = false,
  mapHasFocus = false,
  pinColor = null,
  googleMaps,
}) {
  if (showLabel && (isSelected || isHovered)) {
    return toGoogleIcon(getMapV2LabeledTeardropPinSvg(index, title, pinColor), googleMaps);
  }
  const faded = mapHasFocus && !isSelected && !isHovered;
  return googleDotIconFromSvg(index, isSelected, isHovered, googleMaps, faded, pinColor);
}

/** Hotspot map pin — category color; full-name auto-width label when shown. */
export function googleHotspotCategoryPinIcon({
  title = "",
  showLabel = false,
  isSelected = false,
  isHovered = false,
  mapHasFocus = false,
  pinColor,
  googleMaps,
}) {
  if (showLabel && (isSelected || isHovered)) {
    return toGoogleIcon(getHotspotLabeledTeardropPinSvg(title, pinColor), googleMaps);
  }
  const faded = mapHasFocus && !isSelected && !isHovered;
  return googleDotIconFromSvg(0, isSelected, isHovered, googleMaps, faded, pinColor);
}

/** @deprecated Use googleMapV2ListingPinIcon */
export function googleClassicLabeledPinIcon(title, showLabel, googleMaps) {
  return googleMapV2ListingPinIcon({ title, showLabel, googleMaps });
}
